import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Groupper, Header, Icon, Input, Button, Pagination, Table } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import { Request } from '../SiteClasses';
import { useUser } from "../hooks";
import UserAccess from "../UserAccess";
import moment from 'moment';
import API from '../API';
import classNames from 'classnames';

const PAGE_SIZE = 20;

enum StatusFilter{
	PENDING = 0,
	APPROVED = 1,
	SENT = 2,
	REJECTED = 3,
	DELETED = 4,
	FINALIZED = 5,
}

var RequestList = ()=>{
	var { hasAccess } = useUser();
	var [requests, setRequests] = useState<Request[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [search, setSearch] = useState<string>('');
	var [searchTimeout, setSearchTimeout] = useState<any>(null);
	var [page, setPage] = useState<number>(0);
	var [statusFilters, setStatusFilters] = useState<StatusFilter[]>([]);
	var [expressStatusFilters, setExpressStatusFilters] = useState<StatusFilter[]>([]);

	useEffect(()=>{
		get();
	}, []);

	useEffect(()=>{
		if(requests !== null){
			setPage(0);
			get(0, search.length >= 2 ? search : null);
		}
	}, [statusFilters, expressStatusFilters]);

	var get = (get_page?: number, query?: string)=>{
		var p = get_page ?? page;
		var offset = p * PAGE_SIZE;
		var expressOnly = expressStatusFilters.length > 0;
		var activeStatusFilters = expressOnly ? expressStatusFilters : statusFilters;
		setLoading(true);
		API.getRequests(offset, PAGE_SIZE, query, activeStatusFilters, false, expressOnly).then(res=>{
			if(res.error) return setLoadError(res.message);
			setRequests(res.data);
		}).catch(()=>{
			setLoadError('Hubo un error inesperado cargando las solicitudes.');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onSearchChange = (val: string)=>{
		setSearch(val);
		setPage(0);
		if(searchTimeout) clearTimeout(searchTimeout);
		if(val.length > 0 && val.length < 2) return;
		setSearchTimeout(setTimeout(()=>{
			get(0, val.length >= 2 ? val : null);
		}, 500));
	};

	var onPageChange = (p: number)=>{
		setPage(p);
		get(p, search.length >= 2 ? search : null);
	};

	var onStatusFilter = (s: StatusFilter)=>{
		setExpressStatusFilters([]);
		setStatusFilters(prev=>{
			if(prev.includes(s)) return prev.filter(x=>x !== s);
			if(prev.length >= 5) return prev;
			return [...prev, s];
		});
	};

	var onExpressStatusFilter = (s: StatusFilter)=>{
		setStatusFilters([]);
		setExpressStatusFilters(prev=>{
			if(prev.includes(s)) return prev.filter(x=>x !== s);
			if(prev.length >= 4) return prev;
			return [...prev, s];
		});
	};

	if(loadError) return <Header text='Error' subtext={loadError} />;

	return <Groupper title='Solicitudes' fitted style={{ marginTop: 20 }} width={1100} titleRight={(
		<Dropdown icon={null} pointing direction='left' trigger={(
			<Button text='Opciones' color='black' iconRight='caret-down' iconRightStyle={{ marginRight: 0, marginLeft: 5 }} size='small' />
		)}>
			<Dropdown.Menu>
				{hasAccess(UserAccess.INVENTORY.CREATE_INVENTORY_REQUESTS) && (
					<Dropdown.Item as={Link} to='/requests/create'>
						<Icon name='laptop-file' style={{ marginRight: 3 }} /> Solicitud
					</Dropdown.Item>
				)}
				{hasAccess(UserAccess.INVENTORY.CREATE_INVENTORY_REQUESTS) && (
					<Dropdown.Item as={Link} to='/requests/create?mode=express'>
						<Icon name='bolt' style={{ marginRight: 3 }} /> Solicitud express
					</Dropdown.Item>
				)}
			</Dropdown.Menu>
		</Dropdown>
	)}>
		<div style={{ display: 'flex', gap: 10, marginBottom: 10, padding: '10px 10px 0' }}>
			<Input placeholder='Buscar por ID o creador...' value={search} onChange={onSearchChange} style={{ flex: 1 }} />
			<Dropdown icon={null} pointing direction='left' trigger={(
				<Button
					text='Filtros'
					iconRight={(statusFilters.length > 0 || expressStatusFilters.length > 0) ? 'asterisk' : null}
					iconRightStyle={{ color: 'brown', marginLeft: 5 }}
					size='small'
					iconName='filter'
				/>
			)}>
				<Dropdown.Menu>
					<Dropdown.Item text='Limpiar filtros' icon='trash' onClick={()=>{
						setStatusFilters([]);
						setExpressStatusFilters([]);
					}} />
					<Dropdown.Header content='Estatus' />
					{([
						[StatusFilter.PENDING, 'Pendiente'],
						[StatusFilter.APPROVED, 'Aprobado'],
						[StatusFilter.SENT, 'Enviada'],
						[StatusFilter.REJECTED, 'Rechazado'],
						[StatusFilter.DELETED, 'Eliminado'],
					] as [StatusFilter, string][]).map(([s, label])=>(
						<Dropdown.Item key={s} onClick={()=>onStatusFilter(s)}>
							<i className={classNames('icon', {
								'check square': statusFilters.includes(s),
								'square outline': !statusFilters.includes(s),
							})}></i>
							{label}
						</Dropdown.Item>
					))}
					<Dropdown.Header content='Express' />
					{([
						[StatusFilter.PENDING, 'Pendiente'],
						[StatusFilter.APPROVED, 'Aprobada'],
						[StatusFilter.FINALIZED, 'Finalizada'],
						[StatusFilter.REJECTED, 'Rechazada'],
					] as [StatusFilter, string][]).map(([s, label])=>(
						<Dropdown.Item key={`express-${s}`} onClick={()=>onExpressStatusFilter(s)}>
							<i className={classNames('icon', {
								'check square': expressStatusFilters.includes(s),
								'square outline': !expressStatusFilters.includes(s),
							})}></i>
							{label}
						</Dropdown.Item>
					))}
				</Dropdown.Menu>
			</Dropdown>
		</div>
		<Table
			striped
			headers={['ID', 'Comentarios', 'Inicio taquilla' ,'Retorno','Creador', 'Creado', 'Estado', '']}
			collapsingIndexes={[0, 2, 3, 4, 5, 6, 7]}
			centeredIndexes={[2, 3, 4, 5, 6, 7]}
			footer={(requests && (requests.length >= PAGE_SIZE || page !== 0)) && (
				<Table.Cell row colSpan={8}>
					<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
						<Pagination page={page} onPageChange={onPageChange} hasNext={requests.length >= PAGE_SIZE} />
					</div>
				</Table.Cell>
			)}
		>
			{loading || !requests ? (
				<Table.Cell row colSpan={8}>
					<Header loading text='Cargando' />
				</Table.Cell>
			) : requests.length === 0 ? (
				<Table.Cell row className='empty' colSpan={8}>
					No se encontraron registros
				</Table.Cell>
			) : (
				requests.map(r=>{
					var status = r.deleted ? { label: 'Eliminado', color: 'red' } : r.rejected ? { label: 'Rechazado', color: 'red' } : r.finalized ? { label: 'Finalizado', color: 'black' } : r.shipment_id ? { label: 'Enviada', color: 'blue' } : r.approved ? { label: 'Aprobado', color: 'green' } : { label: 'Pendiente', color: null };
					return (
						<Table.Row
							key={r.request_id}
							collapsingIndexes={[0, 2, 3, 4, 5, 6]}
							centeredIndexes={[2, 3, 4, 5, 6]}
							data={[
								r.request_id,
								r.description || <Icon name='minus' />,
								r.is_express ? 'Express' : (r.box_office_start_date ? moment.unix(r.box_office_start_date).format('DD/MMM/YY HH:mm') : <Icon name='minus' />),
								moment.unix(r.date_return).format('DD/MMM/YY HH:mm'),
								r.user_creator_name || <Icon name='minus' />,
								moment.unix(r.date_created).format('DD/MMM/YY HH:mm'),
								<div className={classNames('fr label', status.color)}>{status.label}</div>,
								<Dropdown icon={(
									<div className='fr label' style={{ padding: '3px 10px' }}>
										<Icon name='ellipsis' style={{ margin: 0 }} />
									</div>
								)} className='icon'>
									<Dropdown.Menu>
										<Dropdown.Item as={Link} to={`/requests/${r.request_id}`}>
											<Icon name='eye' style={{ marginRight: 3 }} /> Ver
										</Dropdown.Item>
										{!!r.shipment_id && (
											<Dropdown.Item as={Link} to={`/shipments/${r.shipment_id}`}>
												<Icon name='truck' style={{ marginRight: 3 }} /> Envío
											</Dropdown.Item>
										)}
									</Dropdown.Menu>
								</Dropdown>,
							]}
						/>
					);
				})
			)}
		</Table>
	</Groupper>;
}

export default RequestList;
